/**
 * useCascaderState Hook
 *
 * 使用useReducer统一管理Cascader组件的18个状态
 * 按业务逻辑分组,便于维护
 */

import { Dispatch, useEffect, useReducer } from 'react';
import type { CascaderOption } from '../iCascader';

// ============ State 类型定义 ============

/**
 * Value相关状态
 */
export interface ValueState {
    value: string[] | CascaderOption[];
    isClear: boolean;
}

/**
 * Input相关状态
 */
export interface InputState {
    inputValue: string;
    initInputValue: string;
    inputFocused: boolean;
}

/**
 * Popup相关状态
 */
export interface PopupState {
    popupVisible: boolean;
    popupClassName: string;
    dropDownCls: boolean;
}

/**
 * 平铺模式相关状态
 */
export interface TiledState {
    tabsArr: Array<{ tab: string; key: string; content: CascaderOption[] }>;
    tabsActiveKey: string;
    deepNum: number;
    tiledValue: string[];
    tiledSelectedOptions: CascaderOption[];
}

/**
 * Dropdown相关状态
 */
export interface DropdownState {
    dropdownDom: any;
    dropdownMatchSelectWidth: boolean;
    isMoreTagClick: boolean;
    restData: any[];
}

/**
 * Options相关状态
 */
export interface OptionsState {
    options: CascaderOption[];
    flattenOptions?: CascaderOption[][];
    loadedKeys: string[];
}

/**
 * 完整的Cascader状态
 */
export interface CascaderState {
    valueState: ValueState;
    inputState: InputState;
    popupState: PopupState;
    tiledState: TiledState;
    dropdownState: DropdownState;
    optionsState: OptionsState;
}

// ============ Action 类型定义 ============

export type CascaderAction =
    // Value actions
    | { type: 'SET_VALUE'; payload: string[] | CascaderOption[] }
    | { type: 'CLEAR_VALUE' }
    | { type: 'SET_IS_CLEAR'; payload: boolean }
    // Input actions
    | { type: 'SET_INPUT_VALUE'; payload: string }
    | { type: 'SET_INIT_INPUT_VALUE'; payload: string }
    | { type: 'SET_INPUT_FOCUSED'; payload: boolean }
    | { type: 'CLEAR_INPUT' }
    // Popup actions
    | { type: 'SET_POPUP_VISIBLE'; payload: boolean }
    | { type: 'SET_POPUP_CLASSNAME'; payload: string }
    | { type: 'SET_DROPDOWN_CLS'; payload: boolean }
    // Tiled actions
    | { type: 'SET_TABS_ARR'; payload: TiledState['tabsArr'] }
    | { type: 'SET_TABS_ACTIVE_KEY'; payload: string }
    | { type: 'SET_DEEP_NUM'; payload: number }
    | { type: 'SET_TILED_VALUE'; payload: string[] }
    | { type: 'SET_TILED_SELECTED_OPTIONS'; payload: CascaderOption[] }
    | { type: 'UPDATE_TILED_STATE'; payload: Partial<TiledState> }
    // Dropdown actions
    | { type: 'SET_DROPDOWN_DOM'; payload: any }
    | { type: 'SET_DROPDOWN_MATCH_WIDTH'; payload: boolean }
    | { type: 'SET_IS_MORE_TAG_CLICK'; payload: boolean }
    | { type: 'SET_REST_DATA'; payload: any[] }
    // Options actions
    | { type: 'SET_OPTIONS'; payload: CascaderOption[] }
    | { type: 'SET_FLATTEN_OPTIONS'; payload: CascaderOption[][] }
    | { type: 'ADD_LOADED_KEY'; payload: string }
    | { type: 'SET_LOADED_KEYS'; payload: string[] }

// ============ Reducer 实现 ============

/**
 * Cascader State Reducer
 */
function cascaderReducer(state: CascaderState, action: CascaderAction): CascaderState {
    switch (action.type) {
        // Value actions
        case 'SET_VALUE':
            return {
                ...state,
                valueState: { ...state.valueState, value: action.payload },
            };
        case 'CLEAR_VALUE':
            return {
                ...state,
                valueState: { value: [], isClear: true },
            };
        case 'SET_IS_CLEAR':
            return {
                ...state,
                valueState: { ...state.valueState, isClear: action.payload },
            };

        // Input actions
        case 'SET_INPUT_VALUE':
            return {
                ...state,
                inputState: { ...state.inputState, inputValue: action.payload },
            };
        case 'SET_INIT_INPUT_VALUE':
            return {
                ...state,
                inputState: { ...state.inputState, initInputValue: action.payload },
            };
        case 'SET_INPUT_FOCUSED':
            return {
                ...state,
                inputState: { ...state.inputState, inputFocused: action.payload },
            };
        case 'CLEAR_INPUT':
            return {
                ...state,
                inputState: {
                    inputValue: '',
                    initInputValue: '',
                    inputFocused: false,
                },
            };

        // Popup actions
        case 'SET_POPUP_VISIBLE':
            return {
                ...state,
                popupState: { ...state.popupState, popupVisible: action.payload },
            };
        case 'SET_POPUP_CLASSNAME':
            return {
                ...state,
                popupState: { ...state.popupState, popupClassName: action.payload },
            };
        case 'SET_DROPDOWN_CLS':
            return {
                ...state,
                popupState: { ...state.popupState, dropDownCls: action.payload },
            };

        // Tiled actions
        case 'SET_TABS_ARR':
            return {
                ...state,
                tiledState: { ...state.tiledState, tabsArr: action.payload },
            };
        case 'SET_TABS_ACTIVE_KEY':
            return {
                ...state,
                tiledState: { ...state.tiledState, tabsActiveKey: action.payload },
            };
        case 'SET_DEEP_NUM':
            return {
                ...state,
                tiledState: { ...state.tiledState, deepNum: action.payload },
            };
        case 'SET_TILED_VALUE':
            return {
                ...state,
                tiledState: { ...state.tiledState, tiledValue: action.payload },
            };
        case 'SET_TILED_SELECTED_OPTIONS':
            return {
                ...state,
                tiledState: { ...state.tiledState, tiledSelectedOptions: action.payload },
            };
        case 'UPDATE_TILED_STATE':
            return {
                ...state,
                tiledState: { ...state.tiledState, ...action.payload },
            };

        // Dropdown actions
        case 'SET_DROPDOWN_DOM':
            return {
                ...state,
                dropdownState: { ...state.dropdownState, dropdownDom: action.payload },
            };
        case 'SET_DROPDOWN_MATCH_WIDTH':
            return {
                ...state,
                dropdownState: { ...state.dropdownState, dropdownMatchSelectWidth: action.payload },
            };
        case 'SET_IS_MORE_TAG_CLICK':
            return {
                ...state,
                dropdownState: { ...state.dropdownState, isMoreTagClick: action.payload },
            };
        case 'SET_REST_DATA':
            return {
                ...state,
                dropdownState: { ...state.dropdownState, restData: action.payload },
            };

        // Options actions
        case 'SET_OPTIONS':
            return {
                ...state,
                optionsState: { ...state.optionsState, options: action.payload },
            };
        case 'SET_FLATTEN_OPTIONS':
            return {
                ...state,
                optionsState: { ...state.optionsState, flattenOptions: action.payload },
            };
        case 'ADD_LOADED_KEY':
            return {
                ...state,
                optionsState: {
                    ...state.optionsState,
                    loadedKeys: [...state.optionsState.loadedKeys, action.payload],
                },
            };
        case 'SET_LOADED_KEYS':
            return {
                ...state,
                optionsState: { ...state.optionsState, loadedKeys: action.payload },
            };

        default:
            return state;
    }
}

// ============ Hook 导出 ============

/**
 * useCascaderState Hook
 *
 * @param initialOptions - 初始options
 * @param initialValue - 初始value
 * @param locale - 语言配置
 * @returns [state, dispatch]
 */
export function useCascaderState(
    props: {
        options: CascaderOption[];
        value?: string[] | CascaderOption[];
        defaultValue?: string[] | CascaderOption[];
        popupVisible?: boolean;
        popupClassName?: string;
        inputValue?: string;
        tabsItems?: any[];
        dropdownRender?: any;
        locale: any;
    }
): [CascaderState, Dispatch<CascaderAction>] {
    const {
        options,
        value,
        defaultValue,
        popupVisible,
        popupClassName,
        inputValue,
        tabsItems,
        dropdownRender,
        locale,
    } = props;

    // 初始化state
    const initialTabsArr = tabsItems
        ? tabsItems
        : [{ tab: locale.langMap?.placeholder, key: '1', content: options }];

    const initialState: CascaderState = {
        valueState: {
            value: value || defaultValue || [],
            isClear: false,
        },
        inputState: {
            inputValue: '',
            initInputValue: inputValue || '',
            inputFocused: false,
        },
        popupState: {
            popupVisible: popupVisible || false,
            popupClassName: popupClassName || '',
            dropDownCls: false,
        },
        tiledState: {
            tabsArr: initialTabsArr,
            tabsActiveKey: tabsItems ? tabsItems[1]?.key : '1',
            deepNum: tabsItems ? 1 : 0,
            tiledValue: tabsItems ? [tabsItems[1]?.key] : [],
            tiledSelectedOptions: tabsItems
                ? [
                    {
                        label: tabsItems[0]?.tab,
                        value: tabsItems[1]?.key,
                        children: tabsItems[1]?.content,
                    },
                ]
                : [],
        },
        dropdownState: {
            dropdownDom: dropdownRender,
            dropdownMatchSelectWidth: false,
            isMoreTagClick: false,
            restData: [],
        },
        optionsState: {
            options: options || [],
            flattenOptions: undefined,
            loadedKeys: [],
        }
    };

    const [state, dispatch] = useReducer(cascaderReducer, initialState);

    // 🔧 统一处理异步 props 同步到 state
    useEffect(() => {
        // 1. 同步 tabsItems
        if (tabsItems && tabsItems.length > 0 && state.tiledState.deepNum === 0) {
            dispatch({
                type: 'UPDATE_TILED_STATE',
                payload: {
                    tabsArr: tabsItems.map((item: any) => ({
                        tab: item.tab,
                        key: item.key,
                        content: item.content
                    })),
                    deepNum: 1,
                    tabsActiveKey: tabsItems[1]?.key
                }
            });
        }

        // 2. 同步 options（如果需要）
        if (options && options.length > 0 && state.optionsState.options.length === 0) {
            dispatch({ type: 'SET_OPTIONS', payload: options });
        }

        // 可以继续添加其他异步 props 的同步逻辑...

    }, [tabsItems, options, state.tiledState.deepNum, state.optionsState.options.length]);

    return [state, dispatch];
}
